/**
 * Membership & Promotions Management Component
 * Manages user memberships and promo code assignments
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { firebaseAdminAPI as adminAPI } from './firebaseAdminService';
import { User, Membership, PromoCode } from './mockData';

export const MembershipManagement = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'memberships' | 'promos'>('memberships');
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [selectedMembershipType, setSelectedMembershipType] = useState<'free' | 'premium' | 'enterprise'>('premium');
  const [selectedPromoCode, setSelectedPromoCode] = useState<string>('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersData, membershipsData, promoCodesData] = await Promise.all([
        adminAPI.getUsers(),
        adminAPI.getMemberships(),
        adminAPI.getPromoCodes()
      ]);
      setUsers(usersData);
      setMemberships(membershipsData);
      setPromoCodes(promoCodesData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGrantMembership = async () => {
    if (!selectedUser) {
      alert('Please select a user');
      return;
    }
    
    if (!confirm(`Grant ${selectedMembershipType} membership to this user?`)) return;

    try {
      const success = await adminAPI.updateMembership(selectedUser, selectedMembershipType);
      if (success) {
        await loadData();
        alert(`Membership updated to ${selectedMembershipType}`);
        setSelectedUser('');
      }
    } catch (error) {
      console.error('Error updating membership:', error);
      alert('Error updating membership');
    }
  };

  const handleRevokeMembership = async (userId: string) => {
    if (!confirm('Revoke membership and set to free?')) return;

    try {
      const success = await adminAPI.updateMembership(userId, 'free');
      if (success) {
        await loadData();
        alert('Membership revoked');
      }
    } catch (error) {
      console.error('Error revoking membership:', error);
      alert('Error revoking membership');
    }
  };

  const handleAssignPromoCode = async () => {
    if (!selectedUser || !selectedPromoCode) {
      alert('Please select both user and promo code');
      return;
    }

    try {
      const success = await adminAPI.assignPromoCode(selectedPromoCode, selectedUser);
      if (success) {
        await loadData();
        alert('Promo code assigned successfully');
        setSelectedUser('');
        setSelectedPromoCode('');
      } else {
        alert('Error assigning promo code');
      }
    } catch (error) {
      console.error('Error assigning promo code:', error);
      alert('Error assigning promo code');
    }
  };

  const getUserById = (userId: string) => {
    return users.find(u => u.id === userId);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8f9fa',
      position: 'relative'
    }}>
      {/* Header */}
      <header style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: '#ffffff',
        padding: '20px 40px',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <Link
              to="/x9s4k-portal-72/admin"
              style={{
                color: '#ffffff',
                textDecoration: 'none',
                fontSize: '1.25rem',
                fontWeight: 600
              }}
            >
              ← Dashboard
            </Link>
            <h1 style={{
              fontSize: '2rem',
              fontWeight: 800,
              letterSpacing: '-0.02em',
              margin: 0
            }}>
              Memberships & Promotions
            </h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main style={{
        padding: '40px',
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '10px',
          marginBottom: '30px',
          background: '#ffffff',
          padding: '10px',
          borderRadius: '15px',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
        }}>
          <button
            onClick={() => setActiveTab('memberships')}
            style={{
              flex: 1,
              padding: '12px 24px',
              border: 'none',
              borderRadius: '50px',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              background: activeTab === 'memberships' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#f8f9fa',
              color: activeTab === 'memberships' ? '#ffffff' : '#495057',
              transition: 'all 0.3s'
            }}
          >
            Memberships
          </button>
          <button
            onClick={() => setActiveTab('promos')}
            style={{
              flex: 1,
              padding: '12px 24px',
              border: 'none',
              borderRadius: '50px',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              background: activeTab === 'promos' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#f8f9fa',
              color: activeTab === 'promos' ? '#ffffff' : '#495057',
              transition: 'all 0.3s'
            }}
          >
            Promo Codes
          </button>
        </div>

        {loading ? (
          <div style={{
            background: '#ffffff',
            padding: '60px',
            borderRadius: '15px',
            textAlign: 'center',
            color: '#6c757d',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
          }}>
            Loading...
          </div>
        ) : activeTab === 'memberships' ? (
          <>
            {/* Grant Membership Form */}
            <div style={{
              background: '#ffffff',
              padding: '30px',
              borderRadius: '15px',
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
              marginBottom: '30px'
            }}>
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: 700,
                marginBottom: '20px',
                color: '#1a1a1a'
              }}>
                Grant Membership
              </h2>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '15px',
                marginBottom: '20px'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: 600,
                    color: '#1a1a1a'
                  }}>
                    Select User
                  </label>
                  <select
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 15px',
                      border: '2px solid #e9ecef',
                      borderRadius: '50px',
                      fontSize: '1rem',
                      outline: 'none'
                    }}
                  >
                    <option value="">Choose user...</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.fullName} (@{user.username})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: 600,
                    color: '#1a1a1a'
                  }}>
                    Membership Type
                  </label>
                  <select
                    value={selectedMembershipType}
                    onChange={(e) => setSelectedMembershipType(e.target.value as 'free' | 'premium' | 'enterprise')}
                    style={{
                      width: '100%',
                      padding: '10px 15px',
                      border: '2px solid #e9ecef',
                      borderRadius: '50px',
                      fontSize: '1rem',
                      outline: 'none'
                    }}
                  >
                    <option value="premium">Premium</option>
                    <option value="enterprise">Enterprise</option>
                    <option value="free">Free</option>
                  </select>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-end'
                }}>
                  <button
                    onClick={handleGrantMembership}
                    style={{
                      width: '100%',
                      padding: '10px 24px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '50px',
                      fontSize: '1rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'transform 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    Grant Membership
                  </button>
                </div>
              </div>
            </div>

            {/* Memberships List */}
            <div style={{
              background: '#ffffff',
              borderRadius: '15px',
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
              overflow: 'hidden'
            }}>
              <div style={{
                padding: '20px 30px',
                borderBottom: '2px solid #e9ecef',
                background: '#f8f9fa'
              }}>
                <h2 style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: '#1a1a1a',
                  margin: 0
                }}>
                  Active Memberships ({memberships.length})
                </h2>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse'
                }}>
                  <thead>
                    <tr style={{
                      background: '#f8f9fa',
                      borderBottom: '2px solid #e9ecef'
                    }}>
                      <th style={{ padding: '15px', textAlign: 'left', fontWeight: 700, color: '#1a1a1a' }}>User</th>
                      <th style={{ padding: '15px', textAlign: 'left', fontWeight: 700, color: '#1a1a1a' }}>Type</th>
                      <th style={{ padding: '15px', textAlign: 'left', fontWeight: 700, color: '#1a1a1a' }}>Start Date</th>
                      <th style={{ padding: '15px', textAlign: 'left', fontWeight: 700, color: '#1a1a1a' }}>End Date</th>
                      <th style={{ padding: '15px', textAlign: 'left', fontWeight: 700, color: '#1a1a1a' }}>Auto Renew</th>
                      <th style={{ padding: '15px', textAlign: 'left', fontWeight: 700, color: '#1a1a1a' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {memberships.map((membership) => {
                      const user = getUserById(membership.userId);
                      if (!user) return null;
                      return (
                        <tr
                          key={membership.userId}
                          style={{
                            borderBottom: '1px solid #e9ecef'
                          }}
                        >
                          <td style={{ padding: '15px' }}>
                            <div>
                              <div style={{ fontWeight: 600, color: '#1a1a1a' }}>{user.fullName}</div>
                              <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>@{user.username}</div>
                            </div>
                          </td>
                          <td style={{ padding: '15px' }}>
                            <span style={{
                              padding: '4px 12px',
                              borderRadius: '50px',
                              fontSize: '0.875rem',
                              fontWeight: 600,
                              background: membership.type === 'enterprise' ? '#764ba2' : '#667eea',
                              color: '#ffffff'
                            }}>
                              {membership.type}
                            </span>
                          </td>
                          <td style={{ padding: '15px', color: '#6c757d', fontSize: '0.875rem' }}>
                            {new Date(membership.startDate).toLocaleDateString()}
                          </td>
                          <td style={{ padding: '15px', color: '#6c757d', fontSize: '0.875rem' }}>
                            {membership.endDate ? new Date(membership.endDate).toLocaleDateString() : 'Never'}
                          </td>
                          <td style={{ padding: '15px' }}>
                            <span style={{
                              padding: '4px 12px',
                              borderRadius: '50px',
                              fontSize: '0.875rem',
                              fontWeight: 600,
                              background: membership.autoRenew ? '#28a74520' : '#dc354520',
                              color: membership.autoRenew ? '#28a745' : '#dc3545'
                            }}>
                              {membership.autoRenew ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td style={{ padding: '15px' }}>
                            {membership.type !== 'free' && (
                              <button
                                onClick={() => handleRevokeMembership(membership.userId)}
                                style={{
                                  padding: '6px 12px',
                                  background: '#dc3545',
                                  color: '#ffffff',
                                  border: 'none',
                                  borderRadius: '50px',
                                  fontSize: '0.875rem',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  transition: 'all 0.2s'
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.background = '#c82333';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.background = '#dc3545';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                Revoke
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Assign Promo Code Form */}
            <div style={{
              background: '#ffffff',
              padding: '30px',
              borderRadius: '15px',
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
              marginBottom: '30px'
            }}>
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: 700,
                marginBottom: '20px',
                color: '#1a1a1a'
              }}>
                Assign Promo Code
              </h2>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '15px',
                marginBottom: '20px'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: 600,
                    color: '#1a1a1a'
                  }}>
                    Select User
                  </label>
                  <select
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 15px',
                      border: '2px solid #e9ecef',
                      borderRadius: '50px',
                      fontSize: '1rem',
                      outline: 'none'
                    }}
                  >
                    <option value="">Choose user...</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.fullName} (@{user.username})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: 600,
                    color: '#1a1a1a'
                  }}>
                    Promo Code
                  </label>
                  <select
                    value={selectedPromoCode}
                    onChange={(e) => setSelectedPromoCode(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 15px',
                      border: '2px solid #e9ecef',
                      borderRadius: '50px',
                      fontSize: '1rem',
                      outline: 'none'
                    }}
                  >
                    <option value="">Choose code...</option>
                    {promoCodes.map(promo => (
                      <option key={promo.id} value={promo.id}>
                        {promo.code} ({promo.discount}% off)
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-end'
                }}>
                  <button
                    onClick={handleAssignPromoCode}
                    style={{
                      width: '100%',
                      padding: '10px 24px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '50px',
                      fontSize: '1rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'transform 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    Assign Code
                  </button>
                </div>
              </div>
            </div>

            {/* Promo Codes List */}
            <div style={{
              background: '#ffffff',
              borderRadius: '15px',
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
              overflow: 'hidden'
            }}>
              <div style={{
                padding: '20px 30px',
                borderBottom: '2px solid #e9ecef',
                background: '#f8f9fa'
              }}>
                <h2 style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: '#1a1a1a',
                  margin: 0
                }}>
                  Available Promo Codes ({promoCodes.length})
                </h2>
              </div>
              <div style={{ padding: '20px' }}>
                <div style={{
                  display: 'grid',
                  gap: '20px'
                }}>
                  {promoCodes.map((promo) => (
                    <div
                      key={promo.id}
                      style={{
                        padding: '20px',
                        border: '2px solid #e9ecef',
                        borderRadius: '15px',
                        background: '#f8f9fa'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '15px'
                      }}>
                        <div>
                          <div style={{
                            fontSize: '1.5rem',
                            fontWeight: 800,
                            color: '#667eea',
                            marginBottom: '5px'
                          }}>
                            {promo.code}
                          </div>
                          <div style={{
                            fontSize: '1.25rem',
                            fontWeight: 700,
                            color: '#1a1a1a'
                          }}>
                            {promo.discount}% Discount
                          </div>
                        </div>
                        <div style={{
                          textAlign: 'right'
                        }}>
                          <div style={{
                            fontSize: '0.875rem',
                            color: '#6c757d',
                            marginBottom: '5px'
                          }}>
                            Uses: {promo.currentUses} / {promo.maxUses}
                          </div>
                          <div style={{
                            width: '200px',
                            height: '8px',
                            background: '#e9ecef',
                            borderRadius: '50px',
                            overflow: 'hidden',
                            marginTop: '5px'
                          }}>
                            <div style={{
                              width: `${(promo.currentUses / promo.maxUses) * 100}%`,
                              height: '100%',
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              transition: 'width 0.3s'
                            }} />
                          </div>
                        </div>
                      </div>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '15px',
                        fontSize: '0.875rem',
                        color: '#6c757d'
                      }}>
                        <div>
                          <strong>Valid From:</strong> {new Date(promo.validFrom).toLocaleDateString()}
                        </div>
                        <div>
                          <strong>Valid Until:</strong> {new Date(promo.validUntil).toLocaleDateString()}
                        </div>
                        <div>
                          <strong>Assigned To:</strong> {promo.assignedTo.length} user(s)
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

